import React, { useEffect, useState } from "react";
import { MessageSquare, UserCircle, Clock } from "lucide-react";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { key: "chats", label: "My Chats", icon: MessageSquare },
  { key: "profile", label: "Profile", icon: UserCircle },
];

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [active, setActive] = useState("chats");
  const [chats, setChats] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/messages/inbox");
      setChats(data?.threads || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <DashboardShell
      title={`Hi, ${user?.name?.split(" ")[0] || "there"}`}
      subtitle="Your conversations and profile."
      nav={NAV}
      active={active}
      onNav={setActive}
    >
      {active === "chats" && (
        <div>
          {chats.length === 0 ? (
            <Empty icon={MessageSquare} title="No conversations" text="Message a shop to start chatting." />
          ) : (
            <div className="space-y-3 max-w-2xl" data-testid="chats-list">
              {chats.map((t) => (
                <div key={`${t.shop_id}-${t.customer_id}`} className="bg-card rounded-2xl border border-border p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">{t.shop_name}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" /> {t.last_at ? new Date(t.last_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                  <p className="text-foreground/90">
                    {t.last_from === "owner" ? "Shop: " : "You: "}
                    {t.last_text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {active === "profile" && (
        <div className="max-w-lg bg-card rounded-3xl border border-border p-8" data-testid="profile-panel">
          <div className="flex items-center gap-4 mb-8">
            <span className="grid place-items-center w-16 h-16 rounded-full bg-primary text-primary-foreground text-2xl font-bold uppercase">
              {user?.name?.[0]}
            </span>
            <div>
              <p className="font-display font-bold text-2xl">{user?.name}</p>
              <p className="text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
            </div>
          </div>
          <Detail label="Email" value={user?.email} />
          <Detail label="Phone" value={user?.phone || "—"} />
          <Detail label="Plan" value={(user?.plan || "free").replace(/^./, (c) => c.toUpperCase())} />
          <Detail label="Status" value={(user?.approval_status || "approved").replace(/^./, (c) => c.toUpperCase())} />
        </div>
      )}
    </DashboardShell>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex justify-between py-4 border-t border-border">
      <span className="text-muted-foreground font-medium">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function Empty({ icon: Icon, title, text }) {
  return (
    <div className="grid place-items-center py-24 text-center">
      <div>
        <span className="grid place-items-center w-16 h-16 rounded-full bg-secondary text-primary mx-auto mb-4">
          <Icon className="w-7 h-7" />
        </span>
        <p className="font-display font-bold text-xl">{title}</p>
        <p className="text-muted-foreground mt-1">{text}</p>
      </div>
    </div>
  );
}
